import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays, getDatesBetween, isNotNil, isSameDay } from '@malou-io/package-utils';

import {
    AbstractCsvService,
    DataWithNilExcluded,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { DailyValue, TimeScaleToMetricToDataValues } from '#modules/statistics/models/insights';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';

interface Data {
    actionsInsights: TimeScaleToMetricToDataValues | undefined;
    startDate: Date | null;
    endDate: Date | null;
}

@Injectable()
export class GmbVisibilityCsvInsightsService extends AbstractCsvService<Data> {
    private readonly _statisticsStore = inject(StatisticsStore);

    protected _isDataValid(data: Data): boolean {
        return !!data.actionsInsights && !!data.startDate && !!data.endDate;
    }

    protected override _getData$(): Observable<Data> {
        return of({
            actionsInsights: this._statisticsStore.actionsData(),
            startDate: this._statisticsStore.filters().dates.startDate,
            endDate: this._statisticsStore.filters().dates.endDate,
        });
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return [
            'Date',
            'Google Maps Impressions Desktop',
            'Google Maps Impressions Mobile',
            'Google Search Impressions Desktop',
            'Google Search Impressions Mobile',
            'Calls',
            'Direction Requests',
            'Website Clicks',
            'Menu Clicks',
            'Booking Clicks',
        ];
    }

    protected override _getCsvDataRows({ actionsInsights, startDate, endDate }: DataWithNilExcluded<Data>): CsvAsStringArrays {
        const insightsByDay = actionsInsights?.by_day;
        const dates = getDatesBetween(startDate, endDate).filter(isNotNil);
        return dates.map((date) => {
            const dateStr = date.toLocaleDateString();
            const businessImpressionsDesktopMaps = this._getStringInsightValueByDay(insightsByDay?.business_impressions_desktop_maps, date);
            const businessImpressionsMobileMaps = this._getStringInsightValueByDay(insightsByDay?.business_impressions_mobile_maps, date);
            const businessImpressionsDesktopSearch = this._getStringInsightValueByDay(
                insightsByDay?.business_impressions_desktop_search,
                date
            );
            const businessImpressionsMobileSearch = this._getStringInsightValueByDay(
                insightsByDay?.business_impressions_mobile_search,
                date
            );
            const actionsPhone = this._getStringInsightValueByDay(insightsByDay?.actions_phone, date);
            const actionsDrivingDirections = this._getStringInsightValueByDay(insightsByDay?.actions_driving_directions, date);
            const actionsWebsite = this._getStringInsightValueByDay(insightsByDay?.actions_website, date);
            const actionsMenu = this._getStringInsightValueByDay(insightsByDay?.actions_menu, date);
            const actionsBooking = this._getStringInsightValueByDay(insightsByDay?.actions_booking, date);
            return [
                dateStr,
                businessImpressionsDesktopMaps,
                businessImpressionsMobileMaps,
                businessImpressionsDesktopSearch,
                businessImpressionsMobileSearch,
                actionsPhone,
                actionsDrivingDirections,
                actionsWebsite,
                actionsMenu,
                actionsBooking,
            ];
        });
    }

    private _getStringInsightValueByDay(insights: DailyValue[] | undefined, date: Date): string {
        return insights?.find((insight) => isSameDay(date, new Date(insight.date)))?.value.toString() ?? '';
    }
}
